import {Avatar, Space, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import React from 'react';
import {StyledBankingOptionLabel} from './styles/StyledFormHelpers';
import {useTranslation} from 'react-i18next';
import ImageLabelOption from '../../components/ImageLabelOption';
import {userRolesConstants} from '../../constants';
import {cardsHelpers} from './cards.helpers';
import {textHelpers} from './text.helpers';
import StatusLine from '../../components/StatusLine';

const {ADMIN, MANAGER, SUPPORT, USER} = userRolesConstants;

const userRoles = {
  [ADMIN]: 'userRoles.admin',
  [MANAGER]: 'userRoles.manager',
  [USER]: 'userRoles.user',
  [SUPPORT]: 'userRoles.support',
}

const isEmptyFormValue = (value) => value === null || value === undefined || value === '';

const normalizeFormValue = (value) => {
  if (isEmptyFormValue(value)) return undefined;
  if (Array.isArray(value)) return value.map(normalizeFormValue);
  if (value && typeof value === 'object') {
    // Date-like objects (Date, dayjs, moment) are compared by timestamp.
    if (typeof value.valueOf === 'function' && typeof value.valueOf() === 'number') {
      const timestamp = value.valueOf();
      // An invalid date (NaN) never equals itself, so treat it as empty.
      return Number.isNaN(timestamp) ? undefined : timestamp;
    }
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return {instance: value};
    return Object.keys(value).sort().reduce((acc, key) => {
      const normalized = normalizeFormValue(value[key]);
      if (normalized !== undefined) acc[key] = normalized;
      return acc;
    }, {});
  }
  return value;
};

const normalizedValuesAreEqual = (a, b) => {
  if (a === b) return true;
  // File, Blob and other class instances are compared by reference only.
  if (a?.instance !== undefined || b?.instance !== undefined) return a?.instance === b?.instance;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  return keysA.length === keysB.length
    && keysA.every(key => key in b && normalizedValuesAreEqual(a[key], b[key]));
};

/**
 * Converts a key-value options map into a translated array of option objects for use in select inputs.
 * @param {Object} optionsArray - Map of option values to i18n translation keys.
 * @param {string|number|null} activeValue - Currently selected value, used to set the isActive flag.
 * @param {string} [emptyLabel] - Optional label for an empty/none option prepended to the list.
 * @returns {Array<{isActive: boolean, value: string|number|null, label: string}>} Translated option objects.
 * @example
 * OptionsList({ 1: 'status.active', 2: 'status.inactive' }, 1);
 * // [{ isActive: true, value: 1, label: 'Active' }, { isActive: false, value: 2, label: 'Inactive' }]
 */
export const OptionsList = (optionsArray, activeValue, emptyLabel) => {
  const [t] = useTranslation('main');
  let options = Object.entries(optionsArray);
  if (emptyLabel) {
    options = [
      [null, emptyLabel],
      ...options
    ];
  }
  return options.map(option => {
    let value = option[0];
    let intValue = parseInt(value);
    if (value === `${intValue}`) value = intValue;
    const label = option[1];
    return {
      isActive: activeValue === value,
      value,
      label: t(label)
    }
  });
}

/**
 * Returns a translated list of assignable user role options, optionally excluding the Support role.
 * @param {boolean} [excludeSupport=true] - When true, the Support role is excluded from the returned list.
 * @returns {Array<{value: string, label: string}>} Array of role option objects with translated labels.
 * @example
 * UserRoleOptions(); // [{ value: '0', label: 'User' }, { value: '1', label: 'Manager' }, { value: '2', label: 'Admin' }]
 */
export const UserRoleOptions = (excludeSupport = true) => {
  const [t] = useTranslation('main');
  const roles = Object.entries(userRoles);
  let excludedRoles = [];
  if (excludeSupport) excludedRoles.push(SUPPORT);
  return roles
    .filter(r => !excludedRoles.includes(parseInt(r[0])))
    .map(role => {
    const value = role[0];
    return {
      value,
      label: t(role[1])
    }
  });
};

/**
 * Collection of form utility helpers for building options, handling errors, and formatting form values.
 * @example
 * import { formHelpers } from './form.helpers';
 * const options = formHelpers.getFormattedOptions({ data: categories });
 */
export const formHelpers = {
  /**
   * Builds a sorted, deduplicated array of employee select options, excluding support and bookkeeper roles.
   * @param {Object} options - Configuration options.
   * @param {Array<Object>} options.employees - Array of employee objects to build options from.
   * @param {string} options.employeeEmail - Email of the current user; used to append '(you)' label.
   * @param {Function} options.t - i18next translation function.
   * @param {string} [options.valueKey='email'] - Property name to use as the option value.
   * @param {boolean} [options.excludeEmployeeEmail=false] - When true, excludes the current user from the list.
   * @param {string} [options.idKey='employee_id'] - Property name used for deduplication.
   * @returns {Array<{bank_id: string, value: *, label: React.ReactElement, optionLabel: string}>} Array of employee option objects.
   * @example
   * formHelpers.getEmployeeOptions({ employees, employeeEmail: 'me@example.com', t });
   */
  getEmployeeOptions: ({employees, employeeEmail, t, valueKey = 'email', excludeEmployeeEmail = false, idKey = 'employee_id'}) => {
    employees = Array.isArray(employees)
      ? [...new Map(employees.map(item => [item[idKey], item])).values()]
      : []
    return employees
      .filter(employee =>
        ![userRolesConstants.SUPPORT, userRolesConstants.BOOKKEEPER].includes(employee.role) &&
        !(excludeEmployeeEmail && employee.email === employeeEmail)
      )
      .sort((a, b) => a.full_name.localeCompare(b.full_name))
      .map(employee => {
        const {email, logo, full_name: name} = employee;
        return {
          bank_id: employee?.bank_id,
          value: employee[valueKey],
          label: (
            <Space className='d-flex-child'>
              <Avatar src={logo || undefined} size={24}>
                {textHelpers.getInitials(name)}
              </Avatar>
              {`${name}${employeeEmail === email ? ` (${t('main:you')})` : ''}`}
            </Space>
          ),
          optionLabel: name
        }
      });
  },
  /**
   * Extends employee options with banking status, disabling employees who lack banking details and adding an informational tooltip.
   * @param {Object} options - Configuration options passed through to getEmployeeOptions.
   * @param {Array<Object>} options.employees - Array of employee objects.
   * @param {string} options.employeeEmail - Email of the current user.
   * @param {Function} options.t - i18next translation function.
   * @param {...*} rest - Additional options forwarded to getEmployeeOptions.
   * @returns {Array<Object>} Employee option objects, with disabled flag and tooltip label for employees without bank_id.
   * @example
   * formHelpers.getEmployeeBankingOptions({ employees, employeeEmail: 'me@example.com', t });
   */
  getEmployeeBankingOptions: ({employees, employeeEmail, t, ...rest}) => {
    const options = formHelpers.getEmployeeOptions({employees, employeeEmail, t, ...rest});
    return options.map(option => {
      if (!option.bank_id) {
        return {
          ...option,
          disabled: true,
          label: (
            <StyledBankingOptionLabel>
              {option.label}
              <Tooltip
                placement='bottomLeft'
                styles={{body: {width: 220}}}
                title={t('main:userNeedsBankingDetails')}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </StyledBankingOptionLabel>
          ),
        };
      }
      return option;
    });
  },
  /**
   * Transforms a server error response object into an array of Ant Design form field error descriptors.
   * @param {Object} [errors={}] - Error response object; expects an 'errors' key containing a field-keyed error map.
   * @returns {Array<{name: string, errors: string[]}>} Array of field error objects suitable for form.setFields().
   * @example
   * formHelpers.getFormServerErrorFields({ errors: { email: 'Invalid email' } });
   * // [{ name: 'email', errors: ['Invalid email'] }]
   */
  getFormServerErrorFields: (errors) => {
    errors = errors || {};
    let fields = [];
    if (errors.hasOwnProperty('errors')) {
      const formErrors = errors.errors || {};
      fields = Object.keys(formErrors).map(key => ({
        name: key,
        errors: [formErrors[key]]
      }));
    }
    return fields;
  },
  /**
   * Processes an API error response, setting field-level errors on a form and a global error message when no field errors exist.
   * Understands three server error shapes: a keyed `{data: {errors: {field: message}}}` map, a FastAPI-style
   * `{detail: [{loc, msg}]}` validation array (mapped to field errors when the field exists on the form, otherwise
   * joined into the global message), and a plain `{detail: 'message'}` string (shown as the global message).
   * @param {Object} options - Error handling options.
   * @param {Object} options.form - Ant Design form instance with a setFields method.
   * @param {Object} options.response - Axios error object (its `.response.data` holds the server payload).
   * @param {Function} options.t - i18next translation function.
   * @param {Function} options.setError - Setter for a global error message string.
   * @returns {void}
   * @example
   * formHelpers.handleFormErrors({ form, response: axiosError, t, setError: setErrorMessage });
   */
  handleFormErrors: ({ form, response, t, setError }) => {
    const data = response?.response?.data || {};
    const { detail } = data;

    let formErrors = formHelpers.getFormServerErrorFields(data.data);

    if (formErrors.length === 0 && Array.isArray(detail)) {
      const knownFields = form.getFieldsValue(true);
      formErrors = detail
        .map(item => ({ name: item?.loc?.[item.loc.length - 1], errors: [item?.msg] }))
        .filter(({ name }) => name && knownFields.hasOwnProperty(name));
    }

    form.setFields(formErrors);
    if (formErrors.length > 0) return;

    const detailMessage = Array.isArray(detail) ? detail.map(item => item?.msg).filter(Boolean).join(' ') : detail;
    setError((typeof detailMessage === 'string' && detailMessage) || data.message || t('serverErrorMessage'));
  },
  /**
   * Returns an Ant Design field error descriptor for a logo upload field when the MIME type cannot be detected.
   * @param {Object} errors - Error object from the upload response; expects a message property.
   * @param {string} logoFieldName - Name of the logo form field to attach the error to.
   * @returns {{name: string, errors: string[]}|{}} Field error descriptor, or an empty object if no relevant error.
   * @example
   * formHelpers.getFormLogoErrorField({ message: 'Can not detect the mime type' }, 'company_logo');
   * // { name: 'company_logo', errors: ['Can not detect the mime type'] }
   */
  getFormLogoErrorField: (errors, logoFieldName) => {
    let field = {};
    let errorMessage = errors?.message;
    if (errorMessage === 'Can not detect the mime type') {
      field = {name: logoFieldName, errors: [errorMessage]};
    }
    return field;
  },
  /**
   * Wraps each option's label in an ImageLabelOption component that displays an icon alongside the label text.
   * @param {Array<{image: React.ReactNode, label: string, value: *}>} options - Array of option objects with an image and label.
   * @returns {Array<Object>} Options with label replaced by an ImageLabelOption element.
   * @example
   * formHelpers.getStatusOptions([{ value: 'active', label: 'Active', image: <ActiveIcon /> }]);
   */
  getStatusOptions: (options) => options.map(option => ({
    ...option,
    label: (
      <ImageLabelOption>
        {option.image}
        {option.label}
      </ImageLabelOption>
    )
  })),
  /**
   * Returns only the form values that have changed compared to the initial values, excluding specified fields.
   * @param {Object} options - Comparison options.
   * @param {Object} options.initialValues - Original form values before user edits.
   * @param {Object} options.submittedValues - Form values after the user submitted the form.
   * @param {string[]} [options.excludedFields=[]] - Field keys to always omit from the diff result.
   * @returns {Object} Object containing only the keys whose values differ from the initial values.
   * @example
   * formHelpers.getUpdatedFormValues({ initialValues: { name: 'Old' }, submittedValues: { name: 'New' } });
   * // { name: 'New' }
   */
  getUpdatedFormValues: ({initialValues, submittedValues, excludedFields = []}) => {
    let values = {};
    Object.keys(submittedValues).forEach(key => {
      if (excludedFields.includes(key)) return;
      const submittedValue = submittedValues[key];
      const initialValue = initialValues[key];
      const isEqual = typeof submittedValue === 'object'
        ? JSON.stringify(submittedValue) === JSON.stringify(initialValue)
        : submittedValue === initialValue;
      if (!isEqual) values = {...values, [key]: submittedValues[key]};
    });
    return values;
  },
  /**
   * Maps an array of data objects to a simple value/label options array using configurable property names.
   * @param {Object} [options={}] - Configuration options.
   * @param {Array<Object>} [options.data=[]] - Source data array.
   * @param {string} [options.valuePropName='id'] - Property name to use as the option value.
   * @param {string} [options.labelPropName='name'] - Property name to use as the option label.
   * @returns {Array<{label: *, value: *}>} Array of formatted option objects.
   * @example
   * formHelpers.getFormattedOptions({ data: [{ id: 1, name: 'Travel' }] });
   * // [{ label: 'Travel', value: 1 }]
   */
  getFormattedOptions: ({data = [], valuePropName = 'id', labelPropName = 'name'} = {}) => {
    if (!data) return [];
    return data.map(d => ({label: d[labelPropName], value: d[valuePropName]}));
  },
  /**
   * Converts an array of tag objects into select option objects using tag_name as the label.
   * @param {Array<Object>|null|undefined} tags - Array of tag objects, each with a tag_name property.
   * @param {string} [valuePropName='tag_name'] - Property name to use as the option value.
   * @returns {Array<{label: string, value: *}>} Array of tag option objects, or empty array if tags is falsy.
   * @example
   * formHelpers.getTagOptions([{ tag_name: 'Urgent' }]); // [{ label: 'Urgent', value: 'Urgent' }]
   */
  getTagOptions: (tags, valuePropName = 'tag_name') => {
    if (!tags) return [];
    return tags.map(tag => ({label: tag.tag_name, value: tag[valuePropName]}));
  },
  /**
   * Builds a list of card select options from an array of card objects, formatting the masked PAN for display.
   * @param {Object} [options={}] - Configuration options.
   * @param {Array<Object>} [options.cards=[]] - Array of card objects; only cards with a masked_pan are included.
   * @param {number} [options.symbolsLength=5] - Number of visible digits to show in the masked card number.
   * @param {string} [options.valuePropertyName='card_id'] - Property name to use as the option value.
   * @returns {Array<{label: string, optionLabel: string, value: *}>} Array of card option objects.
   * @example
   * formHelpers.getCardsOptions({ cards: [{ card_id: 'c1', masked_pan: '4111...1111', name: 'Main Card' }] });
   * // [{ label: '...1111 Main Card', optionLabel: '...1111', value: 'c1' }]
   */
  getCardsOptions: ({cards = [], symbolsLength = 5, valuePropertyName = 'card_id'} = {}) => {
    if (!Array.isArray(cards) || cards.length === 0) return [];

    return cards.reduce((options, card) => {
      if (card.masked_pan) {
        const optionLabel = cardsHelpers.hideCardNumber(card.masked_pan, symbolsLength);
        options.push({
          label: `${optionLabel} ${card.name}`,
          optionLabel,
          value: card[valuePropertyName],
        });
      }
      return options;
    }, []);
  },
  /**
   * Converts a status map (keyed by status value, each containing key and color) into select option objects with colored StatusLine images.
   * @param {Object} [statusesMap={}] - Map of status values to their descriptor objects with key and color properties.
   * @returns {Array<{label: string, value: *, image: React.ReactElement}>} Array of status option objects with image and label.
   * @example
   * formHelpers.getStatusOptionsList({ active: { key: 'status.active', color: 'green' } });
   */
  getStatusOptionsList: (statusesMap = {}) => {
    const statuses = Object.entries(statusesMap).reduce((acc, [key, { key: statusKey }]) => {
      acc[key] = statusKey;
      return acc;
    }, {});

    return OptionsList(statuses).map(({ value, ...option }) => ({
      ...option,
      image: <StatusLine color={statusesMap[value]?.color} />,
      value
    }));
  },
  /**
   * Maps data objects to select option objects, with optional filtering by a truthy property and support for composite labels.
   * @param {Object} options - Configuration options.
   * @param {Array<Object>} [options.data=[]] - Source data array.
   * @param {string} [options.valueProp='id'] - Property name for the option value.
   * @param {string|string[]} [options.labelProp='name'] - Property name (or array of names joined by space) for the option label.
   * @param {string} [options.filteredPropName] - When provided, only items where item[filteredPropName] is truthy are included.
   * @returns {Array<{value: *, label: string}>} Array of formatted option objects.
   * @example
   * formHelpers.formatSelectOptions({ data: [{ id: 1, first_name: 'Jane', last_name: 'Doe' }], labelProp: ['first_name', 'last_name'] });
   * // [{ value: 1, label: 'Jane Doe' }]
   */
  formatSelectOptions: ({data = [], valueProp = 'id', labelProp = 'name', filteredPropName}) => {
    let safeData = Array.isArray(data) ? data : [];
    const labelParts = Array.isArray(labelProp) ? labelProp : [labelProp];
    const getLabel = (item) => labelParts.map(prop => item?.[prop]).filter(Boolean).join(' ');

    if (filteredPropName) {
      safeData = safeData.filter(item => item[filteredPropName]);
    }
    return safeData.map(item => ({
      value: item[valueProp],
      label: getLabel(item),
    }));
  },
  /**
   * Finds and returns the label for a given value from an options array, falling back to the value itself if not found.
   * @param {Array<{value: *, label: string}>} [options=[]] - Array of option objects to search.
   * @param {*} value - The value to look up.
   * @returns {string|*} The matched label string, or the original value if no match is found.
   * @example
   * formHelpers.getOptionValueLabel([{ value: 1, label: 'Active' }], 1); // 'Active'
   * formHelpers.getOptionValueLabel([{ value: 1, label: 'Active' }], 99); // 99
   */
  getOptionValueLabel: (options = [], value) =>
    options.find(option => option.value === value)?.label || value,
  /**
   * Formats a moment/dayjs date object to a 'YYYY-MM-DD' string, or returns undefined if the value is falsy.
   * @param {moment.Moment|dayjs.Dayjs|null|undefined} value - Date object to format.
   * @returns {string|undefined} Formatted date string, e.g. '2024-03-15', or undefined if value is falsy.
   * @example
   * formHelpers.getFormattedDateValue(moment('2024-03-15')); // '2024-03-15'
   * formHelpers.getFormattedDateValue(null); // undefined
   */
  getFormattedDateValue: (value) => value ? value.format('YYYY-MM-DD') : undefined,
  /**
   * Checks whether a string value is empty or contains only whitespace.
   * @param {string} [value] - Value to check.
   * @returns {boolean} True if the value is falsy or whitespace-only after trimming.
   * @example
   * formHelpers.isBlank('  '); // true
   * formHelpers.isBlank('note'); // false
   */
  isBlank: (value) => !value?.trim(),
  /**
   * Builds an Ant Design form rule requiring a non-blank (whitespace-trimmed) string value.
   * @param {string} message - Validation error message shown when the value is blank.
   * @returns {{validator: Function}} A rule object usable in a Form.Item's `rules` array.
   * @example
   * rules={[formHelpers.getRequiredTrimmedRule(t('validation.fieldIsRequired'))]}
   */
  getRequiredTrimmedRule: (message) => ({
    validator: (_, value) => (formHelpers.isBlank(value)
      ? Promise.reject(new Error(message))
      : Promise.resolve()),
  }),
  /**
   * Deeply compares two form value sets, treating null, undefined and '' as the same "empty" value
   * and ignoring key order. Date-like values (Date, dayjs, moment) are compared by timestamp.
   * @param {Object} current - Current form values.
   * @param {Object} initial - Values the form opened with.
   * @returns {boolean} True when nothing differs.
   * @example
   * formHelpers.formValuesAreEqual({name: ''}, {name: undefined}); // true
   */
  formValuesAreEqual: (current, initial) =>
    normalizedValuesAreEqual(normalizeFormValue(current ?? {}) ?? {}, normalizeFormValue(initial ?? {}) ?? {}),
}
